/**
 * Reducer for DatePickerPopup2 component state management
 */
import { formatDate, parseDate, dateRegex } from "./dateUtils";
import { validateDateInput } from "./dateValidation";

// Date regex patterns for validation
// const dateRegex = {
//   fullYear: /^\d{1,2}\/\d{1,2}\/\d{4}$/,
//   shortYear: /^\d{1,2}\/\d{1,2}\/\d{2}$/
// };

/**
 * State interface for the DatePickerPopup2 component
 */
export interface DatePickerState {
  popoverActive: boolean;
  displayMonth: number;
  displayYear: number;
  inputValue: string;
  formatError?: string;
  internalValue: Date | null;
  isEditing: boolean;
}

/**
 * Action types for the DatePickerPopup2 reducer
 */
export type DatePickerAction =
  | { type: "SET_DATE"; payload: Date | null }
  | { type: "SET_INPUT_VALUE"; payload: string }
  | {
      type: "SET_INTERNAL_VALUE";
      payload: { date: Date | null; keepInputValue: boolean };
    }
  | { type: "TOGGLE_POPOVER" }
  | { type: "SET_POPOVER"; payload: boolean }
  | { type: "SET_EDITING"; payload: boolean }
  | { type: "SET_FORMAT_ERROR"; payload: string | undefined }
  | { type: "CLEAR_DATE" }
  | { type: "VALIDATE_INPUT" }
  | { type: "SET_MONTH_YEAR"; payload: { month: number; year: number } }
  | { type: "SELECT_TODAY" };

/**
 * Initialize the DatePickerPopup2 state
 * @param initialDate - Initial date value
 * @returns Initial state
 */
export const initDatePickerState = (
  initialDate: Date | null
): DatePickerState => {
  const today = new Date();
  return {
    popoverActive: false,
    displayMonth: initialDate ? initialDate.getMonth() : today.getMonth(),
    displayYear: initialDate ? initialDate.getFullYear() : today.getFullYear(),
    inputValue: formatDate(initialDate),
    formatError: undefined,
    internalValue: initialDate,
    isEditing: false,
  };
};

/**
 * Reducer function for DatePickerPopup2 component
 * @param state - Current state
 * @param action - Action to perform
 * @returns New state
 */
export const datePickerReducer = (
  state: DatePickerState,
  action: DatePickerAction
): DatePickerState => {
  switch (action.type) {
    case "SET_DATE": {
      const date = action.payload;
      return {
        ...state,
        inputValue: formatDate(date),
        internalValue: date,
        formatError: undefined,
        displayMonth: date ? date.getMonth() : state.displayMonth,
        displayYear: date ? date.getFullYear() : state.displayYear,
      };
    }

    case "SET_INPUT_VALUE": {
      const value = action.payload;

      // If empty, clear the date
      if (value === "") {
        return {
          ...state,
          inputValue: "",
          internalValue: null,
          formatError: undefined,
          isEditing: false,
        };
      }

      // Basic validation during typing
      const hasInvalidChars = /[^\d\/]/.test(value);
      let formatError = hasInvalidChars
        ? "Date can only contain numbers and slashes"
        : undefined;

      // Check for invalid year lengths (more than 4 digits)
      if (dateRegex.invalidYearLength.test(value)) {
        formatError = "Year must be 2 or 4 digits";
      }

      // Check if this is a partial input (during backspacing)
      // This includes any input that doesn't match the complete date formats
      // or specifically matches our partial year pattern
      const isPartialInput =
        !dateRegex.fullYear.test(value) && !dateRegex.shortYear.test(value);

      // Check specifically for partial years like "03/16/202"
      const isPartialYear =
        dateRegex.partialYear.test(value) &&
        !dateRegex.fullYear.test(value) &&
        !dateRegex.shortYear.test(value);

      // Check if this is a two-digit year format (e.g., "01/01/23")
      // We want to treat this as a partial input to prevent auto-conversion
      const isTwoDigitYear = dateRegex.shortYear.test(value);

      // For partial inputs, just update the input value and keep editing state
      // Do not attempt to parse the date or update internalValue
      if (isPartialInput || isPartialYear || isTwoDigitYear) {
        return {
          ...state,
          inputValue: value,
          formatError,
          isEditing: true,
        };
      }

      // For complete inputs with 4-digit years, try to parse the date
      // Use allowPartial=true to ensure we don't auto-correct partial inputs
      const parsedDate = parseDate(value, false, true);

      // Check for invalid calendar dates (like 02/30/2023)
      if (!parsedDate && dateRegex.fullYear.test(value)) {
        // This is a complete date format but invalid calendar date
        formatError = "Please enter a valid calendar date";
      }

      // If we got a valid date, update the state but preserve the exact input value
      if (parsedDate) {
        return {
          ...state,
          inputValue: value, // Keep the exact input value, don't format it
          internalValue: parsedDate,
          formatError,
          displayMonth: parsedDate.getMonth(),
          displayYear: parsedDate.getFullYear(),
          isEditing: true, // Keep editing state true to prevent auto-correction
        };
      }

      // Otherwise, just update the input value
      return {
        ...state,
        inputValue: value,
        formatError,
        isEditing: true,
      };
    }

    case "SET_INTERNAL_VALUE": {
      const { date, keepInputValue } = action.payload;
      return {
        ...state,
        internalValue: date,
        formatError: undefined,
        displayMonth: date ? date.getMonth() : state.displayMonth,
        displayYear: date ? date.getFullYear() : state.displayYear,
        inputValue: keepInputValue ? state.inputValue : formatDate(date),
      };
    }

    case "TOGGLE_POPOVER":
      return {
        ...state,
        popoverActive: !state.popoverActive,
      };

    case "SET_POPOVER":
      return {
        ...state,
        popoverActive: action.payload,
      };

    case "SET_EDITING": {
      // If we're entering editing mode, clear format errors
      // If we're exiting editing mode, keep any format errors
      return {
        ...state,
        isEditing: action.payload,
        // Only clear format errors when entering editing mode, not when exiting
        formatError: action.payload ? undefined : state.formatError,
      };
    }

    case "SET_FORMAT_ERROR": {
      return {
        ...state,
        formatError: action.payload,
        // If setting an error, exit editing mode to show it
        isEditing: action.payload ? false : state.isEditing,
      };
    }

    case "CLEAR_DATE":
      return {
        ...state,
        inputValue: "",
        internalValue: null,
        formatError: undefined,
        isEditing: false,
      };

    case "VALIDATE_INPUT": {
      if (!state.inputValue) {
        return {
          ...state,
          formatError: undefined,
          isEditing: false,
        };
      }

      // Check if the input is a complete date with a 4-digit year
      const isCompleteFullYearDate = dateRegex.fullYear.test(state.inputValue);

      // Check if the input is a complete date with a 2-digit year
      const isCompleteTwoDigitYearDate = dateRegex.shortYear.test(
        state.inputValue
      );

      // Check specifically for partial years like "03/16/202"
      const isPartialYear =
        dateRegex.partialYear.test(state.inputValue) &&
        !dateRegex.fullYear.test(state.inputValue) &&
        !dateRegex.shortYear.test(state.inputValue);

      // For partial inputs or partial years, just keep the current value and stay in editing mode
      if (
        (!isCompleteFullYearDate && !isCompleteTwoDigitYearDate) ||
        isPartialYear
      ) {
        return {
          ...state,
          // Keep isEditing true for partial inputs to allow continued editing
          isEditing: true,
        };
      }

      // Validate the input
      const validationError = validateDateInput(state.inputValue, false);

      if (validationError) {
        console.log("Validation error in reducer:", validationError);
        return {
          ...state,
          formatError: validationError,
          internalValue: null,
          isEditing: false, // Exit editing mode to show the error
        };
      }

      // If it's valid, parse the date
      // Always convert to four-digit year format when validating on blur
      const parsedDate = parseDate(state.inputValue, false, false);
      if (parsedDate) {
        // Always format the date with a four-digit year for display consistency
        const formattedValue = formatDate(parsedDate);

        return {
          ...state,
          inputValue: formattedValue,
          internalValue: parsedDate,
          formatError: undefined,
          displayMonth: parsedDate.getMonth(),
          displayYear: parsedDate.getFullYear(),
          isEditing: false, // Exit editing mode after validation
        };
      }

      return {
        ...state,
        isEditing: false,
      };
    }

    case "SET_MONTH_YEAR":
      return {
        ...state,
        displayMonth: action.payload.month,
        displayYear: action.payload.year,
      };

    case "SELECT_TODAY": {
      const today = new Date();
      return {
        ...state,
        inputValue: formatDate(today),
        internalValue: today,
        formatError: undefined,
        displayMonth: today.getMonth(),
        displayYear: today.getFullYear(),
        popoverActive: false,
        isEditing: false,
      };
    }

    default:
      return state;
  }
};
